/**
 * Mathematical Utilities for Monte Carlo Simulations
 * 
 * This module provides core mathematical functions used by both
 * the Lombard and SWR simulation engines:
 * - Deterministic PRNG (Mulberry32)
 * - Normal distribution sampling (Box-Muller)
 * - Percentile calculation
 * - Seed creation from various inputs
 */

/**
 * Mulberry32 PRNG - deterministic seeded random number generator
 * Produces uniform random numbers in [0, 1) given a seed
 * 
 * @param {number} seed - Integer seed value
 * @returns {Function} - Function that returns next random number
 */
export function mulberry32(seed) {
  return function() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

// SWR v5: keep the state in uint32. The legacy generator remains unchanged for Lombard.
export function swrMulberry32(seed) {
  let state = seed >>> 0;
  return function() {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/**
 * Box-Muller transform for normal distribution sampling
 * Converts uniform random numbers to normally distributed values
 * 
 * @param {Function} random - PRNG function returning uniform [0, 1)
 * @param {number} mean - Mean of the normal distribution
 * @param {number} stdDev - Standard deviation of the distribution
 * @returns {number} - Sample from N(mean, stdDev)
 */
export function normalRandom(random, mean, stdDev) {
  const u1 = Math.max(random(), Number.MIN_VALUE);
  const u2 = random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return mean + stdDev * z;
}

/**
 * Create a numeric seed from various input types
 * - null/undefined/empty string → random seed
 * - number → integer seed
 * - string → hash-based seed
 * 
 * @param {number|string|null|undefined} seedInput - Seed input
 * @returns {number} - Integer seed value
 */
export function createSeed(seedInput) {
  if (seedInput === null || seedInput === undefined || seedInput === '') {
    return Math.floor(Math.random() * 0xFFFFFFFF);
  }
  if (typeof seedInput === 'number') {
    return Math.floor(seedInput);
  }
  // Hash string to number
  let hash = 0;
  for (let i = 0; i < seedInput.length; i++) {
    const char = seedInput.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * Compute percentile value from a SORTED array
 * Uses linear interpolation between adjacent values
 * 
 * @param {number[]} sortedArray - Array sorted in ascending order
 * @param {number} p - Percentile (0-100)
 * @returns {number} - Interpolated percentile value
 */
export function percentile(sortedArray, p) {
  if (sortedArray.length === 0) return 0;
  const index = (p / 100) * (sortedArray.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;
  
  if (upper >= sortedArray.length) return sortedArray[sortedArray.length - 1];
  return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
}

/**
 * Pre-compute inflation indices for each year
 * inflationIndex[y] = (1 + inflationRate)^y
 * 
 * @param {number} inflationRate - Annual inflation rate as decimal (e.g., 0.03)
 * @param {number} years - Number of years to compute
 * @returns {Float64Array} - Array of inflation multipliers
 */
export function computeInflationIndices(inflationRate, years) {
  const inflationIndex = new Float64Array(years + 1);
  for (let y = 0; y <= years; y++) {
    inflationIndex[y] = Math.pow(1 + inflationRate, y);
  }
  return inflationIndex;
}

/**
 * Generate random market returns for multiple simulations
 * Applies optional max drawdown cap to returns
 * 
 * @param {Function} random - PRNG function
 * @param {number} numSims - Number of simulations
 * @param {number} years - Years per simulation
 * @param {number} expectedReturn - Mean annual return
 * @param {number} annualVolatility - Standard deviation of returns
 * @param {number|null} maxSingleYearDrawdown - Optional cap on negative returns
 * @returns {number[][]} - Array of return arrays [sim][year]
 */
export function generateReturnsMatrix(
  random, 
  numSims, 
  years, 
  expectedReturn, 
  annualVolatility, 
  maxSingleYearDrawdown
) {
  const returns = [];
  for (let sim = 0; sim < numSims; sim++) {
    const simReturns = [];
    for (let year = 0; year < years; year++) {
      let annualReturn = normalRandom(random, expectedReturn, annualVolatility);
      if (maxSingleYearDrawdown && maxSingleYearDrawdown > 0 && annualReturn < -maxSingleYearDrawdown) {
        annualReturn = -maxSingleYearDrawdown;
      }
      simReturns.push(annualReturn);
    }
    returns.push(simReturns);
  }
  return returns;
}

const MONTHS_PER_YEAR = 12;
export const SWR_PARAMETER_UNCERTAINTY_EVIDENCE_YEARS = 40;
export const SWR_PARAMETER_UNCERTAINTY_METHOD = 'bayesian-reference-normal-inverse-chi-squared';

export function getAnnualLogReturnParameters(expectedReturn, annualVolatility) {
  const safeExpectedReturn = Number.isFinite(expectedReturn) ? expectedReturn : 0;
  const safeAnnualVolatility = Number.isFinite(annualVolatility) ? Math.max(annualVolatility, 0) : 0;
  const annualGrossMean = Math.max(1 + safeExpectedReturn, 0.000001);
  const annualLogVariance = Math.log(
    1 + (safeAnnualVolatility * safeAnnualVolatility) / (annualGrossMean * annualGrossMean)
  );
  const annualLogMean = Math.log(annualGrossMean) - annualLogVariance / 2;

  return {
    annualLogMean,
    annualLogVariance,
  };
}

function getMonthlyReturnParameters(expectedReturn, annualVolatility) {
  const { annualLogMean, annualLogVariance } = getAnnualLogReturnParameters(
    expectedReturn,
    annualVolatility
  );

  return {
    monthlyLogMean: annualLogMean / MONTHS_PER_YEAR,
    monthlyLogVolatility: Math.sqrt(annualLogVariance / MONTHS_PER_YEAR),
  };
}

function gammaRandom(random, shape) {
  if (!Number.isFinite(shape) || shape <= 0) return 0;

  if (shape < 1) {
    const uniform = Math.max(random(), Number.MIN_VALUE);
    return gammaRandom(random, shape + 1) * Math.pow(uniform, 1 / shape);
  }

  const d = shape - (1 / 3);
  const c = 1 / Math.sqrt(9 * d);

  while (true) {
    const normal = normalRandom(random, 0, 1);
    const base = 1 + c * normal;
    if (base <= 0) continue;

    const candidate = base ** 3;
    const uniform = Math.max(random(), Number.MIN_VALUE);
    if (
      uniform < 1 - 0.0331 * (normal ** 4)
      || Math.log(uniform) < 0.5 * normal * normal + d * (1 - candidate + Math.log(candidate))
    ) {
      return d * candidate;
    }
  }
}

/**
 * Draw one set of long-run annual log-return parameters for an SWR path.
 *
 * Verified ETF presets use the actual annual log-return sample statistics and n.
 * Custom/legacy inputs instead use moment-matched log parameters with 40
 * conventional equivalent observations (not an observed sample). With the
 * normal-model reference prior p(mu, sigma^2) proportional to 1 / sigma^2:
 *   sigma^2 | data ~ Inv-chi-squared(n - 1, sampleVariance)
 *   mu | sigma^2, data ~ Normal(sampleMean, sigma^2 / n)
 *
 * One posterior draw is kept fixed for the entire path so parameter
 * uncertainty remains persistent at long horizons.
 */
export function sampleSWRAnnualLogReturnParameters(
  random,
  expectedReturn,
  annualVolatility,
  evidenceYears = SWR_PARAMETER_UNCERTAINTY_EVIDENCE_YEARS,
  observedLogStatistics = null
) {
  if (observedLogStatistics && (!Number.isInteger(observedLogStatistics.n) || observedLogStatistics.n < 3
    || !Number.isFinite(observedLogStatistics.mean) || !Number.isFinite(observedLogStatistics.variance)
    || observedLogStatistics.variance < 0)) throw new Error('Statistiche log-rendimenti non valide.');
  const central = observedLogStatistics
    ? { annualLogMean: observedLogStatistics.mean, annualLogVariance: observedLogStatistics.variance }
    : getAnnualLogReturnParameters(expectedReturn, annualVolatility);
  const {
    annualLogMean: centralAnnualLogMean,
    annualLogVariance: centralAnnualLogVariance,
  } = central;
  const sampleSize = observedLogStatistics?.n ?? Math.max(3, Math.round(evidenceYears));
  const degreesOfFreedom = sampleSize - 1;

  if (centralAnnualLogVariance <= 0) {
    return {
      annualLogMean: centralAnnualLogMean,
      annualLogVariance: 0,
      annualArithmeticMean: Math.exp(centralAnnualLogMean) - 1,
      annualArithmeticVolatility: 0,
      evidenceYears: sampleSize,
      method: SWR_PARAMETER_UNCERTAINTY_METHOD,
    };
  }

  const chiSquaredDraw = Math.max(
    2 * gammaRandom(random, degreesOfFreedom / 2),
    Number.MIN_VALUE
  );
  const annualLogVariance = (
    degreesOfFreedom * centralAnnualLogVariance
  ) / chiSquaredDraw;
  const annualLogMean = normalRandom(
    random,
    centralAnnualLogMean,
    Math.sqrt(annualLogVariance / sampleSize)
  );
  const annualGrossMean = Math.exp(annualLogMean + annualLogVariance / 2);
  const annualArithmeticVolatility = Math.sqrt(
    (Math.exp(annualLogVariance) - 1)
    * Math.exp(2 * annualLogMean + annualLogVariance)
  );

  return {
    annualLogMean,
    annualLogVariance,
    annualArithmeticMean: annualGrossMean - 1,
    annualArithmeticVolatility,
    evidenceYears: sampleSize,
    method: SWR_PARAMETER_UNCERTAINTY_METHOD,
  };
}

function applyAnnualDrawdownCap(monthlyReturns, maxSingleYearDrawdown) {
  if (!maxSingleYearDrawdown || maxSingleYearDrawdown <= 0 || monthlyReturns.length === 0) {
    return monthlyReturns;
  }

  const minAnnualGrowth = Math.max(1 - maxSingleYearDrawdown, 0.000001);
  const annualGrowth = monthlyReturns.reduce((compound, monthlyReturn) => (
    compound * Math.max(1 + monthlyReturn, 0.000001)
  ), 1);

  if (annualGrowth >= minAnnualGrowth) return monthlyReturns;

  const adjustmentFactor = Math.pow(minAnnualGrowth / annualGrowth, 1 / monthlyReturns.length);
  return monthlyReturns.map(monthlyReturn => (
    Math.max((1 + monthlyReturn) * adjustmentFactor - 1, -0.999999)
  ));
}

/**
 * Generate one calendar year of monthly market returns.
 * Expected return and volatility remain annual inputs; they are converted to
 * monthly equivalents before sampling, then optionally capped at the annual
 * calendar-year drawdown level used by the SWR form.
 *
 * @param {Function} random - PRNG function
 * @param {number} expectedReturn - Mean annual return
 * @param {number} annualVolatility - Annual standard deviation
 * @param {number|null} maxSingleYearDrawdown - Optional calendar-year downside cap
 * @param {number} monthsInYear - Number of monthly samples to generate
 * @returns {number[]} - Monthly returns for one simulated year
 */
export function generateMonthlyReturnsForYear(
  random,
  expectedReturn,
  annualVolatility,
  maxSingleYearDrawdown,
  monthsInYear = MONTHS_PER_YEAR
) {
  const { monthlyLogMean, monthlyLogVolatility } = getMonthlyReturnParameters(expectedReturn, annualVolatility);
  return generateMonthlyReturnsForYearFromLogParameters(
    random,
    monthlyLogMean * MONTHS_PER_YEAR,
    (monthlyLogVolatility ** 2) * MONTHS_PER_YEAR,
    maxSingleYearDrawdown,
    monthsInYear
  );
}

export function generateMonthlyReturnsForYearFromLogParameters(
  random,
  annualLogMean,
  annualLogVariance,
  maxSingleYearDrawdown,
  monthsInYear = MONTHS_PER_YEAR
) {
  const monthlyLogMean = annualLogMean / MONTHS_PER_YEAR;
  const monthlyLogVolatility = Math.sqrt(
    Math.max(Number(annualLogVariance) || 0, 0) / MONTHS_PER_YEAR
  );
  const monthCount = Math.max(0, Math.round(monthsInYear));
  const monthlyReturns = [];

  for (let month = 0; month < monthCount; month++) {
    const monthlyLogReturn = normalRandom(random, monthlyLogMean, monthlyLogVolatility);
    monthlyReturns.push(Math.exp(monthlyLogReturn) - 1);
  }

  return applyAnnualDrawdownCap(monthlyReturns, maxSingleYearDrawdown);
}

/**
 * Generate monthly market returns for multiple SWR simulations.
 * The outer shape mirrors generateReturnsMatrix, but each simulation contains
 * years * 12 monthly returns instead of one return per year.
 *
 * @param {Function} random - PRNG function
 * @param {number} numSims - Number of simulations
 * @param {number} years - Years per simulation
 * @param {number} expectedReturn - Mean annual return
 * @param {number} annualVolatility - Annual standard deviation
 * @param {number|null} maxSingleYearDrawdown - Optional calendar-year downside cap
 * @returns {number[][]} - Array of return arrays [sim][month]
 */
export function generateMonthlyReturnsMatrix(
  random,
  numSims,
  years,
  expectedReturn,
  annualVolatility,
  maxSingleYearDrawdown,
  options = {}
) {
  const {
    parameterUncertainty = false,
    evidenceYears = SWR_PARAMETER_UNCERTAINTY_EVIDENCE_YEARS,
    observedLogStatistics = null,
    onParameterSample,
  } = options;
  const returns = [];
  for (let sim = 0; sim < numSims; sim++) {
    const simReturns = [];
    const annualLogParameters = parameterUncertainty
      ? sampleSWRAnnualLogReturnParameters(
        random,
        expectedReturn,
        annualVolatility,
        evidenceYears,
        observedLogStatistics
      )
      : {
        ...getAnnualLogReturnParameters(expectedReturn, annualVolatility),
        annualArithmeticMean: expectedReturn,
        annualArithmeticVolatility: annualVolatility,
      };
    onParameterSample?.(annualLogParameters, sim);

    for (let year = 0; year < years; year++) {
      simReturns.push(...generateMonthlyReturnsForYearFromLogParameters(
        random,
        annualLogParameters.annualLogMean,
        annualLogParameters.annualLogVariance,
        maxSingleYearDrawdown
      ));
    }
    returns.push(simReturns);
  }
  return returns;
}
